/**
 * Mehrsprachigkeit ohne Bibliothek.
 *
 * Præsto hat einige hundert Texte, zwei Sprachen und keine Absicht, Wörterbücher
 * nachzuladen. Dafür wäre ein i18n-Framework mehr Konfiguration als Nutzen: Es
 * brächte Namensräume, Nachladelogik und eine eigene Interpolationssprache mit,
 * von denen wir nichts brauchen.
 *
 * Stattdessen: flache Schlüssel-Objekte je Sprache, zur Bauzeit gegeneinander
 * geprüft. Deutsch ist die Quelle, Englisch ist als `Record<keyof typeof de,
 * string>` typisiert — ein fehlender oder übriger englischer Schlüssel ist ein
 * Build-Fehler, keine Lücke, die erst im Betrieb auffällt.
 *
 * Client und Server teilen dasselbe Wörterbuch. Der Server braucht es für
 * Fehlermeldungen und E-Mails; beide sollen in der Sprache ankommen, in der der
 * Empfänger die Oberfläche bedient.
 */
import { de } from './de/index.js';
import { en } from './en/index.js';
export const LANGUAGES = ['de', 'en'];
/** Deutsch ist Quellsprache und Rückfall: Hier fehlt garantiert kein Text. */
export const DEFAULT_LANGUAGE = 'de';
/** Wie die Sprache in der Oberfläche heißt — stets in sich selbst benannt. */
export const LANGUAGE_NAMES = {
    de: 'Deutsch',
    en: 'English',
};
const DICTIONARIES = { de, en };
export function isLanguage(value) {
    return value === 'de' || value === 'en';
}
/**
 * Erste verstandene Sprache aus einem `Accept-Language`-Kopf.
 *
 * Bewusst grob: Gewichtungen werden gelesen, Regionalvarianten auf die
 * Basissprache verkürzt (`en-GB` → `en`). Wer eine andere Sprache verlangt,
 * bekommt `null` und damit die Vorgabe des Aufrufers.
 */
export function languageFromHeader(header) {
    if (!header)
        return null;
    const wanted = header
        .split(',')
        .map((part) => {
        const [tag, ...params] = part.trim().split(';');
        const q = params.find((p) => p.trim().startsWith('q='));
        return {
            tag: (tag ?? '').trim().toLowerCase().split('-')[0] ?? '',
            weight: q ? Number.parseFloat(q.trim().slice(2)) || 0 : 1,
        };
    })
        .filter((entry) => entry.tag)
        .sort((a, b) => b.weight - a.weight);
    for (const entry of wanted) {
        if (isLanguage(entry.tag))
            return entry.tag;
    }
    return null;
}
function fill(text, values) {
    if (!values)
        return text;
    return text.replace(/\{(\w+)\}/g, (match, name) => name in values ? String(values[name]) : match);
}
// Übersetzer sind zustandslos und werden pro Sprache genau einmal gebaut. Das
// spart nicht die Welt, hält aber Referenzen stabil — React-Hooks, die `t` als
// Abhängigkeit führen, rendern sonst bei jedem Durchlauf neu.
const cache = new Map();
export function translator(language) {
    const cached = cache.get(language);
    if (cached)
        return cached;
    const words = DICTIONARIES[language];
    const lookup = (key, values) => {
        // Rückfall auf Deutsch statt auf den Schlüssel: Ein deutscher Satz in einer
        // englischen Oberfläche ist unschön, ein sichtbares `board.share.title`
        // wäre kaputt.
        const text = words[key] ?? de[key] ?? key;
        return fill(text, values);
    };
    // `Object.assign` statt Zuweisung an die Funktion: Nur so behält der Typ
    // seine Aufrufsignatur und bekommt die beiden Felder dazu.
    const built = Object.assign(lookup, {
        language,
        plural: (count, one, many, values) => lookup(count === 1 ? one : many, { count, ...values }),
    });
    cache.set(language, built);
    return built;
}
/** Kurzform für Stellen ohne Kontext — Jobs, Skripte, Protokolle. */
export const tDefault = translator(DEFAULT_LANGUAGE);
//# sourceMappingURL=index.js.map